import {
  createContext,
  MouseEvent,
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CustomSankeyLayerProps,
  ResponsiveSankey,
  SankeyLabelComponent,
} from "@nivo/sankey";
import { Text } from "@nivo/text";
import dayjs from "dayjs";
import { useTooltip } from "@nivo/tooltip";
import { buildClasses } from "../utils/buildClasses";
import { RyeNoResults } from "../rye/RyeNoResults";
import { RyeMainContentHeader } from "../rye/RyeMainContentHeader";
import { RyeMainContentSpacer } from "../rye/RyeMainContentSpacer";
import { RyeMainContentCard } from "../rye/RyeMainContentCard";
import { ScrollContainerContext } from "../rye/RyeMainContentContainerBase";
import {
  RyeChartTooltip,
  RyeChartTooltipProvider,
} from "../rye/RyeChartTooltip";
import { RyeToggle } from "../rye/RyeToggle";
import { DollarAmount } from "../common/DollarAmount";
import { RyeMainContentSplitPane } from "../rye/RyeMainContentSplitPane";
import { TransactionsDrilldownPane } from "../common/TransactionsDrilldownPane";
import {
  MoneyFlowHighlightLayer,
  MONEY_FLOW_DIMMED_NODE_OPACITY,
} from "./MoneyFlowHighlightLayer";
import { MoneyFlowSelectionLayer } from "./MoneyFlowSelectionLayer";
import { ReportDateRangeControl } from "./ReportDateRangeControl";
import { resolveDateRange } from "./reportDateRange";
import type { MoneyFlowReportState } from "./cashFlowState";
import {
  getMoneyFlowDrilldown,
  MoneyFlowSelection,
  MoneyFlowData,
  MoneyFlowLink,
  MoneyFlowNode,
  TOTAL_FLOW_NODE_ID,
  useMoneyFlowData,
} from "./useMoneyFlowData";

const ChartCurrencyContext = createContext("USD");
const ChartSelectionContext = createContext<MoneyFlowSelection | null>(null);

type ChartHoverActions = Pick<
  CustomSankeyLayerProps<MoneyFlowNode, MoneyFlowLink>,
  "setCurrentNode" | "setCurrentLink"
>;
const ChartHoverActionsContext =
  createContext<MutableRefObject<ChartHoverActions | null> | null>(null);

function SelectionLayer(
  props: CustomSankeyLayerProps<MoneyFlowNode, MoneyFlowLink>,
) {
  const selection = useContext(ChartSelectionContext);
  const hoverActions = useContext(ChartHoverActionsContext);
  const { setCurrentNode, setCurrentLink } = props;
  // Nivo exposes hover setters to layers, but not to custom label components.
  useEffect(() => {
    if (!hoverActions) return;
    hoverActions.current = { setCurrentNode, setCurrentLink };
    return () => {
      hoverActions.current = null;
    };
  }, [hoverActions, setCurrentNode, setCurrentLink]);
  return <MoneyFlowSelectionLayer {...props} selection={selection} />;
}

// The chart fills the card, and only grows past it when the busiest column
// needs more room than that — at which point the card scrolls rather than
// squeezing the labels together.
const MIN_CHART_HEIGHT_PX = 320;
const HEIGHT_PER_NODE_PX = 38;
// The card's shared 18px padding above and below the chart.
const CHART_VERTICAL_PADDING_PX = 36;

const LABEL_FONT_SIZE_PX = 12;
const LABEL_FONT = `${LABEL_FONT_SIZE_PX}px InterVariable, sans-serif`;
// Each side margin is sized to the labels that actually land in it, so a side
// with short labels doesn't reserve width the chart could be using. Past the
// cap, labels truncate instead of widening the margin further.
const LABEL_PADDING_PX = 12;
const LABEL_EDGE_GUTTER_PX = 8;
const MIN_SIDE_MARGIN_PX = 40;
const MAX_SIDE_MARGIN_PX = 250;
// Any label at or under this fits its own side's margin, because that margin
// was sized from it. Only labels past the cap need truncating.
const MAX_LABEL_WIDTH_PX =
  MAX_SIDE_MARGIN_PX - LABEL_PADDING_PX - LABEL_EDGE_GUTTER_PX;

function sideMarginPx(labels: string[]): number {
  const widest = labels.reduce(
    (widestSoFar, label) => Math.max(widestSoFar, measureLabelWidthPx(label)),
    0,
  );
  const needed = Math.ceil(widest) + LABEL_PADDING_PX + LABEL_EDGE_GUTTER_PX;
  return Math.min(MAX_SIDE_MARGIN_PX, Math.max(MIN_SIDE_MARGIN_PX, needed));
}

const LABEL_COLOR = "#334155"; // on-surface-700
const LINK_OPACITY = 0.35;
const NODE_BORDER_RADIUS_PX = 3;
const CHART_THEME = {
  text: {
    fontFamily: "InterVariable, sans-serif",
    fontSize: LABEL_FONT_SIZE_PX,
    fill: LABEL_COLOR,
  },
  tooltip: {
    container: {
      background: "transparent",
      boxShadow: "none",
      padding: 0,
    },
  },
};

// SVG text has no equivalent of CSS text-overflow, so the truncation has to
// happen on the string. Canvas measurement is used rather than a character
// count because the labels are emoji-prefixed and proportionally spaced, so
// character counts are a poor proxy for width. The context re-resolves the font
// on each call, so measurements correct themselves once InterVariable loads.
let labelMeasureContext: CanvasRenderingContext2D | null | undefined;

function measureLabelWidthPx(text: string): number {
  if (labelMeasureContext === undefined) {
    labelMeasureContext = document.createElement("canvas").getContext("2d");
    if (labelMeasureContext) labelMeasureContext.font = LABEL_FONT;
  }
  return labelMeasureContext ? labelMeasureContext.measureText(text).width : 0;
}

function truncateLabelToWidth(text: string, maxWidthPx: number): string {
  if (measureLabelWidthPx(text) <= maxWidthPx) return text;

  // Split into code points first, so a cut never lands inside an emoji's
  // surrogate pair and leaves a broken glyph behind.
  const characters = Array.from(text);

  // Longest prefix that still fits once the ellipsis is appended.
  let fits = 0;
  let tooWide = characters.length;
  while (fits < tooWide) {
    const midpoint = Math.ceil((fits + tooWide) / 2);
    const candidate = `${characters.slice(0, midpoint).join("")}…`;
    if (measureLabelWidthPx(candidate) <= maxWidthPx) {
      fits = midpoint;
    } else {
      tooWide = midpoint - 1;
    }
  }
  return `${characters.slice(0, fits).join("").trimEnd()}…`;
}

/**
 * The waist carries one label on each side: everything to its left is inflow,
 * everything to its right is outflow. Nivo draws a single label per node, so
 * the pair is drawn here as a layer instead, and the node's own label is
 * suppressed.
 */
function WaistLabels({
  nodes,
  setCurrentNode,
}: CustomSankeyLayerProps<MoneyFlowNode, MoneyFlowLink>): JSX.Element | null {
  const currency = useContext(ChartCurrencyContext);
  const { showTooltipFromEvent, hideTooltip } = useTooltip();
  const waist = nodes.find((node) => node.id === TOTAL_FLOW_NODE_ID);
  if (!waist) return null;

  const showTotalTooltip = (event: MouseEvent<SVGElement>) =>
    showTooltipFromEvent(
      <ChartTooltip
        label={waist.nodeLabel}
        amount={waist.value}
        currency={currency}
      />,
      event,
      "left",
    );

  const hoverHandlers = {
    onMouseEnter: (event: MouseEvent<SVGElement>) => {
      setCurrentNode(waist);
      showTotalTooltip(event);
    },
    onMouseMove: showTotalTooltip,
    onMouseLeave: () => {
      setCurrentNode(null);
      hideTooltip();
    },
  };

  const centerY = waist.y + waist.height / 2;
  const labelStyle = {
    fontFamily: CHART_THEME.text.fontFamily,
    fontSize: LABEL_FONT_SIZE_PX,
    fill: LABEL_COLOR,
    pointerEvents: "auto" as const,
  };

  return (
    <>
      {/* Allow hover emphasis on the central total, but no click selection. */}
      <rect
        x={waist.x}
        y={waist.y}
        width={waist.width}
        height={waist.height}
        fill="transparent"
        style={{ cursor: "default" }}
        {...hoverHandlers}
      />
      <text
        x={waist.x - LABEL_PADDING_PX}
        y={centerY}
        textAnchor="end"
        dominantBaseline="central"
        style={labelStyle}
        {...hoverHandlers}
      >
        Total Inflow
      </text>
      <text
        x={waist.x + waist.width + LABEL_PADDING_PX}
        y={centerY}
        textAnchor="start"
        dominantBaseline="central"
        style={labelStyle}
        {...hoverHandlers}
      >
        Total Outflow
      </text>
    </>
  );
}

/**
 * Rendered inside the card rather than alongside it, because it measures the
 * card's scroll viewport through ScrollContainerContext — which only the card's
 * descendants can see.
 */
function MoneyFlowChart({
  data,
  selection,
  onSelect,
}: {
  data: MoneyFlowData;
  selection: MoneyFlowSelection | null;
  onSelect: (selection: MoneyFlowSelection) => void;
}): JSX.Element {
  const hoverActions = useRef<ChartHoverActions | null>(null);
  const renderLabel = useCallback<
    SankeyLabelComponent<MoneyFlowNode, MoneyFlowLink>
  >(
    function Label({ node, style, ...props }) {
      const currency = useContext(ChartCurrencyContext);
      const { showTooltipFromEvent, hideTooltip } = useTooltip();
      const showLabelTooltip = (event: MouseEvent<SVGTextElement>) =>
        showTooltipFromEvent(
          <ChartTooltip
            label={node.nodeLabel}
            amount={node.value}
            currency={currency}
          />,
          event,
          "left",
        );
      if (node.id === TOTAL_FLOW_NODE_ID) return null;
      return (
        <Text
          {...props}
          className={buildClasses(
            "outline-none",
            "focus-visible:outline",
            "focus-visible:outline-2",
            "focus-visible:outline-purple-500",
          )}
          style={{ ...style, pointerEvents: "auto", cursor: "pointer" }}
          role="button"
          tabIndex={0}
          aria-label={`Show ${node.nodeLabel} transactions`}
          onMouseEnter={(event) => {
            hoverActions.current?.setCurrentLink(null);
            hoverActions.current?.setCurrentNode(node);
            showLabelTooltip(event);
          }}
          onMouseMove={showLabelTooltip}
          onMouseLeave={() => {
            hoverActions.current?.setCurrentNode(null);
            hideTooltip();
          }}
          onClick={() => onSelect(node.id)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onSelect(node.id);
            }
          }}
        />
      );
    },
    [onSelect],
  );

  // The chart has no intrinsic height, so without measuring it can only be
  // given a fixed one, which leaves the card half empty on a tall screen. The
  // viewport's clientHeight is the visible area and doesn't change when the
  // chart inside it grows, so there's no feedback loop.
  const scrollContainer = useContext(ScrollContainerContext);
  const [availableHeightPx, setAvailableHeightPx] = useState(0);
  useEffect(() => {
    if (!scrollContainer) return;
    const measure = () =>
      setAvailableHeightPx(
        scrollContainer.clientHeight - CHART_VERTICAL_PADDING_PX,
      );
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(scrollContainer);
    return () => observer.disconnect();
  }, [scrollContainer]);

  // Fill the card, unless the busiest column needs more room than the card has,
  // in which case take the room and let the card scroll.
  const neededHeightPx = Math.max(
    MIN_CHART_HEIGHT_PX,
    data.maxColumnNodeCount * HEIGHT_PER_NODE_PX,
  );
  const chartHeightPx = Math.max(neededHeightPx, availableHeightPx);

  const chartMargin = useMemo(
    () => ({
      top: 16,
      bottom: 16,
      left: sideMarginPx(data.outerLabels.left),
      right: sideMarginPx(data.outerLabels.right),
    }),
    [data.outerLabels],
  );

  // d3-sankey orders each node's links by where the node at the other end sits,
  // which is what keeps bands from crossing — but only when it is left to
  // choose the order itself. Nivo's sort="input" switches that off, pinning
  // links to the order the hook emitted them, which is how a reversed
  // category's band ended up climbing across Total Income's.
  //
  // Passing a comparator instead re-enables the link ordering while still
  // fixing the *node* order within each column: nivo only disables link sorting
  // for the literal "input", and d3 only re-sorts nodes by position when no
  // comparator was given. This one reproduces the hook's emission order, which
  // is already meaningful — categories descend by size, and the balancing nodes
  // sink to the bottom of their column.
  const nodeOrder = useMemo(
    () => new Map(data.nodes.map((node, index) => [node.id, index])),
    [data.nodes],
  );
  const sortNodes = useCallback(
    (a: MoneyFlowNode, b: MoneyFlowNode) =>
      (nodeOrder.get(a.id) ?? 0) - (nodeOrder.get(b.id) ?? 0),
    [nodeOrder],
  );

  // Most nodes belong in the column d3-sankey infers for them, which is one
  // past their furthest-back source. The two exceptions are flagged by the hook
  // as a columnOverride, since it's the side that knows what each node means.
  const alignNode = useCallback(
    (node: { depth?: number }, columnCount: number) => {
      const override = (node as unknown as MoneyFlowNode).columnOverride;
      if (override === undefined) return node.depth ?? 0;
      if (override === "lastColumn") return columnCount - 1;
      return override;
    },
    [],
  );

  return (
    <RyeChartTooltipProvider
      style={{ height: `${chartHeightPx}px` }}
      className={buildClasses(
        "[&_rect]:cursor-pointer",
        "[&_path]:cursor-pointer",
      )}
    >
      <ChartHoverActionsContext.Provider value={hoverActions}>
        <ChartCurrencyContext.Provider value={data.currency}>
          <ChartSelectionContext.Provider value={selection}>
            <ResponsiveSankey<MoneyFlowNode, MoneyFlowLink>
              data={{ nodes: data.nodes, links: data.links }}
              onClick={(section) => {
                const groupId =
                  "source" in section ? section.drilldownNodeId : section.id;
                if (groupId !== TOTAL_FLOW_NODE_ID) onSelect(groupId);
              }}
              margin={chartMargin}
              align={alignNode}
              sort={sortNodes}
              colors={(node) => node.nodeColor}
              theme={CHART_THEME}
              nodeThickness={16}
              nodeSpacing={18}
              nodeHoverOpacity={MONEY_FLOW_DIMMED_NODE_OPACITY}
              nodeHoverOthersOpacity={MONEY_FLOW_DIMMED_NODE_OPACITY}
              nodeBorderWidth={0}
              nodeBorderRadius={NODE_BORDER_RADIUS_PX}
              linkOpacity={LINK_OPACITY}
              linkHoverOpacity={0.6}
              linkHoverOthersOpacity={0.1}
              linkBlendMode="normal"
              enableLinkGradient={true}
              layers={[
                "links",
                "nodes",
                MoneyFlowHighlightLayer,
                "labels",
                WaistLabels,
                SelectionLayer,
              ]}
              label={(node) =>
                // The waist is labelled on both sides by WaistLabels.
                node.id === TOTAL_FLOW_NODE_ID
                  ? ""
                  : truncateLabelToWidth(node.nodeLabel, MAX_LABEL_WIDTH_PX)
              }
              labelComponent={renderLabel}
              labelPosition="outside"
              labelOrientation="horizontal"
              labelPadding={LABEL_PADDING_PX}
              labelTextColor={LABEL_COLOR}
              nodeTooltip={({ node }) => (
                <ChartTooltip
                  label={node.nodeLabel}
                  amount={node.value}
                  currency={data.currency}
                />
              )}
              linkTooltip={({ link }) => (
                <ChartTooltip
                  label={
                    link.source.id === link.drilldownNodeId
                      ? link.source.nodeLabel
                      : link.target.nodeLabel
                  }
                  amount={link.value}
                  currency={data.currency}
                />
              )}
            />
          </ChartSelectionContext.Provider>
        </ChartCurrencyContext.Provider>
      </ChartHoverActionsContext.Provider>
    </RyeChartTooltipProvider>
  );
}

export function MoneyFlowReport({
  state,
  setState,
}: {
  state: MoneyFlowReportState;
  setState: (
    update: (prev: MoneyFlowReportState) => MoneyFlowReportState,
  ) => void;
}): JSX.Element {
  const { dateRange, includeUnreviewed } = state;

  // Memoized so they keep a stable identity across renders — useMoneyFlowData
  // keys its work off of them.
  const { beginDate, endDate } = useMemo(
    () => resolveDateRange(dateRange, dayjs()),
    [dateRange],
  );

  const data = useMoneyFlowData(beginDate, endDate, includeUnreviewed);
  const [selection, setSelection] = useState<MoneyFlowSelection | null>(null);
  const drilldown = useMemo(
    () => getMoneyFlowDrilldown(data, selection),
    [data, selection],
  );

  useEffect(() => {
    setSelection(null);
  }, [beginDate, endDate, includeUnreviewed]);

  return (
    <>
      <RyeMainContentHeader title="Sankey flow" roundedTl={false}>
        <div
          className={buildClasses("flex", "items-center", "gap-3", "w-full")}
        >
          <ReportDateRangeControl
            presets={[
              "this-month",
              "last-month",
              "year-to-date",
              "last-12-months",
            ]}
            range={dateRange}
            onChange={(update) =>
              setState((prev) => ({
                ...prev,
                dateRange: update(prev.dateRange),
              }))
            }
          />
          <div
            className={buildClasses(
              "flex",
              "items-center",
              "gap-2",
              "ml-auto",
              "shrink-0",
            )}
          >
            <span className={buildClasses("text-sm", "text-on-surface-600")}>
              Show unreviewed
            </span>
            <RyeToggle
              size="sm"
              initIsChecked={includeUnreviewed}
              onChange={(checked) =>
                setState((prev) => ({ ...prev, includeUnreviewed: checked }))
              }
            />
          </div>
        </div>
      </RyeMainContentHeader>
      <RyeMainContentSpacer direction="h" />
      <RyeMainContentSplitPane
        bottomPaneLabel="transactions pane"
        bottomPane={
          drilldown ? (
            <TransactionsDrilldownPane
              onClose={() => setSelection(null)}
              key={selection}
              groupLabel={drilldown.groupLabel}
              transactions={drilldown.transactions}
              explanation={drilldown.explanation}
            />
          ) : null
        }
      >
        <RyeMainContentCard
          roundedTl={false}
          roundedTr={false}
          roundedBl={false} // meets the left nav
          roundedBr={drilldown === null}
        >
          {data.nodes.length === 0 ? (
            <EmptyState includeUnreviewed={includeUnreviewed} />
          ) : (
            <MoneyFlowChart
              data={data}
              selection={drilldown ? selection : null}
              onSelect={setSelection}
            />
          )}
        </RyeMainContentCard>
      </RyeMainContentSplitPane>
    </>
  );
}

function ChartTooltip({
  label,
  amount,
  currency,
}: {
  label: string;
  amount: number;
  currency: string;
}): JSX.Element {
  return (
    <RyeChartTooltip>
      <div
        className={buildClasses(
          "flex",
          "items-center",
          "gap-2",
          "bg-surface",
          "border",
          "border-surface-600",
          "shadow-glow",
          "rounded-md",
          "px-3",
          "py-2",
          "text-sm",
          "text-on-surface-700",
        )}
      >
        <span>{label}</span>
        <span className={buildClasses("font-medium")}>
          <DollarAmount
            n={amount}
            size="sm"
            showDollarSign={true}
            showMinus={false}
            showPlus={false}
            align="left"
            currencyCode={currency}
          />
        </span>
      </div>
    </RyeChartTooltip>
  );
}

function EmptyState({
  includeUnreviewed,
}: {
  includeUnreviewed: boolean;
}): JSX.Element {
  return (
    <RyeNoResults
      icon="account_tree"
      title="Nothing to show for these dates"
      description={
        includeUnreviewed
          ? "There is no net money flow from reviewed categories or unreviewed transactions for this date range."
          : "There are no reviewed transactions in categories for this date range."
      }
    />
  );
}
